import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import {
  extractTeacherRecord,
  extractTeacherToken,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSignupContext,
  setTeacherSession,
} from "../../features/teacher/teacherSlice";
import {
  TeacherAuthField,
  TeacherAuthLayout,
  TeacherFeedbackMessage,
} from "./TeacherUI";
import { getTeacherAuthErrorMessage } from "../../utils/uiErrorMessages";

const initialFormData = {
  email: "",
  password: "",
};

export default function TeacherLogin() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalMessage, setGeneralMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const redirectPath = location.state?.from?.pathname || "/teacherdashboard";
  const entryMessage = useMemo(
    () => String(location.state?.message || "").trim(),
    [location.state]
  );

  const loginMutation = useMutation({
    mutationFn: teacherApi.login,
    onSuccess: (payload) => {
      const teacherToken = extractTeacherToken(payload);
      const teacherRecord = extractTeacherRecord(payload, {
        email: formData.email,
      });

      if (!teacherToken) {
        setGeneralMessage(
          "Invalid Credentials"
        );
        return;
      }

      dispatch(
        setTeacherSession({
          token: teacherToken,
          teacher: teacherRecord,
        })
      );
      dispatch(clearTeacherSignupContext());
      navigate(redirectPath, { replace: true });
    },
    onError: (error) => {
      setFieldErrors(error.fieldErrors || {});
      setGeneralMessage(getTeacherAuthErrorMessage(error, "login"));
    },
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (fieldErrors[name]) {
      setFieldErrors((previous) => ({
        ...previous,
        [name]: "",
      }));
    }

    if (generalMessage) {
      setGeneralMessage("");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFieldErrors({});
    setGeneralMessage("");

    const nextErrors = {};

    if (!formData.email.trim()) {
      nextErrors.email = "Email is required.";
    }

    if (!formData.password) {
      nextErrors.password = "Password is required.";
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    loginMutation.mutate({
      email: formData.email.trim(),
      password: formData.password,
    });
  };

  return (
    <TeacherAuthLayout
      title="Teacher Portal"
      subtitle="Welcome back. Log in to continue to your teacher workspace."
      closeTo="/"
      footer={
        <>
          <p className="teacher-auth-footer-text">
            New teacher? <Link className="teacher-auth-link" to="/teachersignup">Create an account</Link>
          </p>
        </>
      }
    >
      {entryMessage ? (
        <TeacherFeedbackMessage variant="info">{entryMessage}</TeacherFeedbackMessage>
      ) : null}
      {generalMessage ? (
        <TeacherFeedbackMessage variant="error">{generalMessage}</TeacherFeedbackMessage>
      ) : null}

      <form className="teacher-auth-form" onSubmit={handleSubmit} noValidate>
        <TeacherAuthField
          id="teacher-email"
          label="Email address"
          icon={Mail}
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          autoComplete="email"
          placeholder="Enter your email"
          error={fieldErrors.email}
        />

        <TeacherAuthField
          id="teacher-password"
          label="Password"
          icon={Lock}
          name="password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleChange}
          autoComplete="current-password"
          placeholder="Enter your password"
          error={fieldErrors.password}
          trailingAction={
            <button
              className="teacher-auth-toggle"
              type="button"
              onClick={() => setShowPassword((previous) => !previous)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />

        <div className="teacher-auth-row" style={{ justifyContent: "center" }}>
          <span className="teacher-auth-note">
            {loginMutation.isPending
              ? "Verifying your teacher credentials..."
              : "Empower learning every day. Log in to manage your classes and students."}
          </span>
        </div>

        <button className="teacher-auth-button" type="submit" disabled={loginMutation.isPending}>
          {loginMutation.isPending ? "Logging in..." : "Login"}
        </button>

        <button
          className="teacher-auth-button"
          type="button"
          onClick={() =>
            navigate("/teacherlogin-otp", {
              state: {
                from: location.state?.from,
              },
            })
          }
          disabled={loginMutation.isPending}
          style={{
            background: "#ffffff",
            color: "#08059B",
            border: "1px solid rgba(8, 5, 155, 0.18)",
          }}
        >
          Teacher login with OTP
        </button>

        <div
          className="teacher-auth-row"
          style={{ justifyContent: "center", marginBottom: "-18px" }}
        >
          <Link className="teacher-auth-link" to="/teacherforgot-password">
            Forgot password?
          </Link>
        </div>
      </form>
    </TeacherAuthLayout>
  );
}
